import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { BookNotify } from "@/components/BookNotify";
import { ArrowRight, BookOpen, Clock, ExternalLink, GraduationCap, FileText } from "lucide-react";

const USS_WICHITA = "https://customer-assets.emergentagent.com/job_morse-chronicles/artifacts/4mgcbt8b_USS%20Wichita%20Gray.jpg";
const SOLDIER_VOTE_COVER = "https://customer-assets.emergentagent.com/job_morse-chronicles/artifacts/zq0pn4um_The%20Soldier%20Vote%20Cover.jpg";
const SOLDIER_VOTE_SAMPLE = "https://customer-assets.emergentagent.com/job_morse-chronicles/artifacts/chbbxz2x_soldier_vote_chapter1.pdf";
const SCHOLAR_URL = "https://scholar.google.com/citations?user=WypJ78AAAAAJ&hl=en&oi=sra";
const SCHOLAR_SOLDIER_VOTE = "https://scholar.google.com/citations?user=WypJ78AAAAAJ&hl=en&oi=sra";
const VOTING_COVER = "https://static.prod-images.emergentagent.com/jobs/b16508fb-0ce2-444b-ad39-aeb00d336b86/images/ebc6270a8312cb2c289de19c674bd90b1d309c4c8b2c316e0c236e444adaf2b4.jpeg";
const FLIGHT_COVER = "https://static.prod-images.emergentagent.com/jobs/b16508fb-0ce2-444b-ad39-aeb00d336b86/images/bcff0d48331eb785bf0fc83e34b73c837475a9d0e6d07457db6d70d9c430b9ef.jpeg";
const WAKE_COVER = "https://customer-assets.emergentagent.com/job_morse-chronicles/artifacts/s72m069g_USS%20Wichita.jpg";

export default function Books() {
  useEffect(() => {
    document.title = "Books by Donald Stephen Inbody — The Soldier Vote, The Morse Chronicles & More";
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Book",
          "@id": "https://inbody.net/books#soldier-vote",
          "name": "The Soldier Vote: War, Politics, and the Ballot in America",
          "author": { "@type": "Person", "name": "Donald S. Inbody", "url": "https://inbody.net" },
          "genre": ["Political Science", "Military History"],
          "publisher": { "@type": "Organization", "name": "Palgrave Macmillan" },
          "datePublished": "2016",
          "inLanguage": "en",
          "url": "https://inbody.net/books",
          "description": "An authoritative empirical study of military voting rights in wartime America, from the Civil War to the present day. Examines the systematic disenfranchisement of soldiers and its impact on American civil-military policy."
        },
        {
          "@type": "Book",
          "@id": "https://inbody.net/books#voting-from-afar",
          "name": "Voting From Afar: The Origins of Absentee Voting in America",
          "author": { "@type": "Person", "name": "Donald S. Inbody", "url": "https://inbody.net" },
          "genre": ["Political Science", "History"],
          "inLanguage": "en",
          "publisher": { "@type": "Organization", "name": "Palgrave Macmillan" },
          "description": "A history of military and overseas absentee voting in America, carrying the argument of The Soldier Vote forward and bringing it up to the present fight over voting by mail."
        },
        {
          "@type": "Book",
          "@id": "https://inbody.net/books#flight-over-water",
          "name": "Flight Over Water: FDR's Secret Flight to Casablanca",
          "author": { "@type": "Person", "name": "Donald S. Inbody", "url": "https://inbody.net" },
          "genre": ["Military History", "History"],
          "inLanguage": "en",
          "publisher": { "@type": "Organization", "name": "University of Missouri Press" },
          "isPartOf": { "@type": "BookSeries", "name": "American Military Experience" },
          "description": "The story of Franklin Roosevelt's secret wartime journey to Casablanca — the first time a sitting American president flew in an aircraft and the first to leave the country during wartime."
        },
        {
          "name": "Wake of the Falcon",
          "author": { "@type": "Person", "name": "Donald S. Inbody", "url": "https://inbody.net" },
          "genre": ["Historical Fiction", "Naval Fiction", "Naval Thriller"],
          "inLanguage": "en",
          "description": "A U.S. Navy captain aboard an obsolete cruiser must rescue a kidnapped American spy from a German raider hidden among seventeen thousand islands in the Dutch East Indies, 1914. Armed only with a civilian mathematician, a network of amateur radio operators, and orders that forbid him from starting a war.",
          "isPartOf": {
            "@type": "BookSeries",
            "name": "The Morse Chronicles",
            "url": "https://inbody.net/books"
          }
        }
      ]
    });
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, []);

  return (
    <div className="bg-[#F5F0E8]">
      <SEO
        title="Books by Donald Stephen Inbody"
        description="Books by Donald Stephen Inbody: The Soldier Vote (Palgrave Macmillan), forthcoming Voting From Afar (Palgrave) and Flight Over Water: FDR's Secret Flight to Casablanca (University of Missouri Press), and the naval thriller Wake of the Falcon — currently querying representation."
        canonical="https://inbody.net/books"
        keywords="The Soldier Vote, Voting From Afar, Flight Over Water, FDR Casablanca, Wake of the Falcon, The Morse Chronicles, Donald Stephen Inbody books, Dutch East Indies, WWI naval fiction, Palgrave Macmillan, University of Missouri Press, civil-military relations, military voting, political science, naval thriller"
      />
      <Navbar />

      {/* Page Header — USS Wichita as background */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${USS_WICHITA})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/90 via-[#0A1628]/70 to-[#0A1628]/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <p className="overline text-[#C5A059] mb-3">Publications</p>
          <h1 data-testid="books-page-title" className="font-serif text-5xl font-black text-[#F5F0E8] mb-4">
            Books by Donald S. Inbody
          </h1>
          <p className="text-[#F5F0E8]/60 max-w-xl leading-relaxed">
            Rigorous political science scholarship, naval history research, and an upcoming novel — the written works of Donald S. Inbody.
          </p>
        </div>
      </section>

      {/* ── Published Academic Work ── */}
      <section data-testid="academic-section" className="py-24 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-12">
            <div className="h-px flex-1 bg-[#0A1628]/15" />
            <p className="overline">Published — Non-Fiction</p>
            <div className="h-px flex-1 bg-[#0A1628]/15" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Actual Book Cover */}
            <div className="book-cover flex justify-center">
              <div className="book-inner w-56 shadow-2xl">
                <img
                  src={SOLDIER_VOTE_COVER}
                  alt="The Soldier Vote — Book Cover"
                  data-testid="soldier-vote-cover"
                  className="w-full h-auto block"
                />
              </div>
            </div>

            {/* Content */}
            <div>
              <p className="overline mb-3">Political Science · Military History</p>
              <h2 className="font-serif text-3xl font-bold text-[#0A1628] mb-2">
                The Soldier Vote
              </h2>
              <p className="text-[#C5A059] font-serif text-lg italic mb-6">
                War, Politics, and the Ballot in America
              </p>
              <div className="space-y-4 text-[#2D3142] leading-relaxed mb-8">
                <p>
                  An authoritative examination of how the United States has grappled with the fundamental question of wartime democracy: should soldiers vote, and if so, how?
                </p>
                <p>
                  Drawing on congressional records, military dispatches, and personal correspondence, Inbody traces the evolution of soldier voting rights from the Civil War through the modern era — revealing the political battles that shaped every conflict.
                </p>
                <p>
                  A landmark contribution to the study of civil-military relations and American political history.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { label: "Author", value: "Donald S. Inbody" },
                  { label: "Publisher", value: "Palgrave Macmillan" },
                  { label: "Genre", value: "Political Science" },
                  { label: "Focus", value: "Civil-Military Relations" },
                ].map(({ label, value }) => (
                  <div key={label} className="border border-[#0A1628]/12 p-4 bg-white">
                    <div className="text-xs text-[#2D3142]/50 uppercase tracking-widest mb-1">{label}</div>
                    <div className="text-sm font-semibold text-[#0A1628]">{value}</div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <a
                  href={SOLDIER_VOTE_SAMPLE}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="soldier-vote-sample-btn"
                  className="inline-flex items-center gap-2 bg-[#C5A059] text-[#0A1628] px-5 py-2.5 text-sm font-bold hover:bg-[#C5A059]/90 transition-colors"
                >
                  <FileText size={14} /> Want to Read a Sample?
                </a>
                <a
                  href={SCHOLAR_SOLDIER_VOTE}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="soldier-vote-scholar-link"
                  className="inline-flex items-center gap-2 border border-[#0A1628]/30 text-[#2D3142] px-5 py-2.5 text-sm font-semibold hover:border-[#0A1628] hover:text-[#0A1628] transition-colors"
                >
                  <GraduationCap size={14} /> View on Google Scholar
                </a>
                <Link
                  to="/blog?category=Political%20Science"
                  data-testid="soldier-vote-blog-link"
                  className="inline-flex items-center gap-2 bg-[#0A1628] text-[#F5F0E8] px-5 py-2.5 text-sm font-semibold hover:bg-[#2D3142] transition-colors"
                >
                  Related Blog Posts <ArrowRight size={14} />
                </Link>
                <Link
                  to="/press"
                  className="inline-flex items-center gap-2 border border-[#0A1628] text-[#0A1628] px-5 py-2.5 text-sm font-semibold hover:bg-[#0A1628] hover:text-[#F5F0E8] transition-colors"
                >
                  Press Inquiries <ExternalLink size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Forthcoming ── */}
      <section data-testid="forthcoming-section" className="py-24 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-12">
            <div className="h-px flex-1 bg-[#0A1628]/15" />
            <p className="overline">Forthcoming</p>
            <div className="h-px flex-1 bg-[#0A1628]/15" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

            {/* Voting From Afar — Under Contract */}
            <div data-testid="forthcoming-voting-from-afar" className="border border-[#0A1628]/12 border-t-2 border-t-[#C5A059] bg-white p-8 flex flex-col gap-4">
              <div className="flex justify-center">
                <div className="relative w-36 aspect-[2/3] shadow-xl overflow-hidden" style={{ background: "#06111f" }}>
                  <img src={VOTING_COVER} alt="Voting From Afar — cover mockup" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#000]/85 via-[#000]/20 to-[#000]/40" />
                  <div className="absolute top-0 left-0 right-0 px-3 pt-3">
                    <div className="border-t border-[#C5A059]/60 pt-1.5">
                      <p className="text-[#C5A059]/85 text-[7px] uppercase tracking-[0.25em] font-mono text-center">Palgrave Macmillan</p>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 px-3 pb-4 text-center">
                    <h3 className="font-serif text-base font-black text-[#F5F0E8] leading-tight drop-shadow-lg">Voting From<br />Afar</h3>
                    <div className="h-px w-6 bg-[#C5A059]/70 mx-auto my-1.5" />
                    <p className="text-[#F5F0E8]/70 text-[7px] font-mono uppercase tracking-widest">Donald S. Inbody</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 bg-[#C5A059] text-[#0A1628] text-[10px] font-mono font-bold uppercase tracking-widest px-3 py-1">
                  <BookOpen size={11} /> Under Contract
                </span>
                <span className="text-[10px] font-mono text-[#2D3142]/40 bg-[#0A1628]/5 px-3 py-1 border border-[#0A1628]/10 uppercase tracking-widest">
                  Non-Fiction
                </span>
              </div>
              <div>
                <p className="text-[10px] text-[#C5A059]/70 uppercase tracking-[0.25em] font-mono mb-2">Political Science · Military Voting</p>
                <h2 className="font-serif text-2xl font-bold text-[#0A1628] leading-tight mb-1">
                  Voting From Afar
                </h2>
                <p className="font-serif text-base text-[#0A1628]/60 italic mb-4">
                  The Origins and Evolution of Absentee Voting in America
                </p>
                <div className="h-px w-12 bg-[#C5A059]/40 mb-4" />
                <p className="text-[#2D3142]/65 text-sm leading-relaxed">
                  A history of military and overseas absentee voting in America, carrying the argument of <em>The Soldier Vote</em> forward and bringing it up to the present fight over voting by mail.
                </p>
              </div>
              <div className="mt-auto pt-4 border-t border-[#0A1628]/8">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] text-[#C5A059]/50 uppercase tracking-widest mb-1">Publisher</div>
                    <div className="text-xs font-semibold text-[#2D3142]/80">Palgrave Macmillan</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#C5A059]/50 uppercase tracking-widest mb-1">Expected</div>
                    <div className="text-xs font-semibold text-[#2D3142]/80">Summer 2028</div>
                  </div>
                </div>
              </div>
              <BookNotify book="Voting From Afar" testid="voting-from-afar" />
            </div>

            {/* Flight Over Water — Under Contract */}
            <div data-testid="forthcoming-flight-over-water" className="border border-[#0A1628]/12 border-t-2 border-t-[#C5A059] bg-white p-8 flex flex-col gap-4">
              <div className="flex justify-center">
                <div className="relative w-36 aspect-[2/3] shadow-xl overflow-hidden" style={{ background: "#06111f" }}>
                  <img src={FLIGHT_COVER} alt="Flight Over Water — cover mockup" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#000]/85 via-[#000]/20 to-[#000]/40" />
                  <div className="absolute top-0 left-0 right-0 px-3 pt-3">
                    <div className="border-t border-[#C5A059]/60 pt-1.5">
                      <p className="text-[#C5A059]/85 text-[7px] uppercase tracking-[0.25em] font-mono text-center">American Military Experience</p>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 px-3 pb-4 text-center">
                    <h3 className="font-serif text-base font-black text-[#F5F0E8] leading-tight drop-shadow-lg">Flight Over<br />Water</h3>
                    <div className="h-px w-6 bg-[#C5A059]/70 mx-auto my-1.5" />
                    <p className="text-[#F5F0E8]/70 text-[7px] font-mono uppercase tracking-widest">Donald S. Inbody</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 bg-[#C5A059] text-[#0A1628] text-[10px] font-mono font-bold uppercase tracking-widest px-3 py-1">
                  <BookOpen size={11} /> Under Contract
                </span>
                <span className="text-[10px] font-mono text-[#2D3142]/40 bg-[#0A1628]/5 px-3 py-1 border border-[#0A1628]/10 uppercase tracking-widest">
                  Non-Fiction
                </span>
              </div>
              <div>
                <p className="text-[10px] text-[#C5A059]/70 uppercase tracking-[0.25em] font-mono mb-2">Military History · American Military Experience</p>
                <h2 className="font-serif text-2xl font-bold text-[#0A1628] leading-tight mb-1">
                  Flight Over Water
                </h2>
                <p className="font-serif text-base text-[#0A1628]/60 italic mb-4">
                  FDR's Secret Flight to Casablanca
                </p>
                <div className="h-px w-12 bg-[#C5A059]/40 mb-4" />
                <p className="text-[#2D3142]/65 text-sm leading-relaxed">
                  The story of Franklin Roosevelt's secret wartime journey to Casablanca — the first time a sitting American president flew in an aircraft and the first to leave the country during wartime.
                </p>
              </div>
              <div className="mt-auto pt-4 border-t border-[#0A1628]/8">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] text-[#C5A059]/50 uppercase tracking-widest mb-1">Publisher</div>
                    <div className="text-xs font-semibold text-[#2D3142]/80">University of Missouri Press</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#C5A059]/50 uppercase tracking-widest mb-1">Expected</div>
                    <div className="text-xs font-semibold text-[#2D3142]/80">Fall 2028</div>
                  </div>
                </div>
              </div>
              <BookNotify book="Flight Over Water" testid="flight-over-water" />
            </div>

            {/* Wake of the Falcon — In Query */}
            <div data-testid="forthcoming-wake-of-the-falcon" className="border border-[#0A1628]/12 bg-white p-8 flex flex-col gap-4">
              <div className="flex justify-center">
                <div className="relative w-36 aspect-[2/3] shadow-xl overflow-hidden" style={{ background: "#06111f" }}>
                  <img src={WAKE_COVER} alt="Wake of the Falcon — USS Wichita cover mockup" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#000]/85 via-[#000]/20 to-[#000]/40" />
                  <div className="absolute top-0 left-0 right-0 px-3 pt-3">
                    <div className="border-t border-[#C5A059]/60 pt-1.5">
                      <p className="text-[#C5A059]/85 text-[7px] uppercase tracking-[0.25em] font-mono text-center">The Morse Chronicles</p>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 px-3 pb-4 text-center">
                    <h3 className="font-serif text-base font-black text-[#F5F0E8] leading-tight drop-shadow-lg">Wake of<br />the Falcon</h3>
                    <div className="h-px w-6 bg-[#C5A059]/70 mx-auto my-1.5" />
                    <p className="text-[#F5F0E8]/70 text-[7px] font-mono uppercase tracking-widest">Donald S. Inbody</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 bg-[#0A1628]/8 border border-[#0A1628]/15 text-[#0A1628]/70 text-[10px] font-mono font-bold uppercase tracking-widest px-3 py-1">
                  <Clock size={11} /> In Query
                </span>
                <span className="text-[10px] font-mono text-[#2D3142]/40 bg-[#0A1628]/5 px-3 py-1 border border-[#0A1628]/10 uppercase tracking-widest">
                  Fiction
                </span>
              </div>
              <div>
                <p className="text-[10px] text-[#C5A059]/70 uppercase tracking-[0.25em] font-mono mb-2">Historical Fiction · Naval Thriller</p>
                <h2 className="font-serif text-2xl font-bold text-[#0A1628] leading-tight mb-1">
                  Wake of the Falcon
                </h2>
                <p className="font-serif text-base text-[#0A1628]/60 italic mb-4">
                  The Morse Chronicles
                </p>
                <div className="h-px w-12 bg-[#C5A059]/40 mb-4" />
                <p className="text-[#2D3142]/65 text-sm leading-relaxed">
                  A 1914 naval thriller: a U.S. Navy captain aboard an obsolete cruiser must rescue a kidnapped American spy from a German raider hidden among seventeen thousand islands of the Dutch East Indies — armed only with a civilian mathematician, a network of amateur radio operators, and orders that forbid him from starting a war.
                </p>
              </div>
              <div className="mt-auto pt-4 border-t border-[#0A1628]/8">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] text-[#C5A059]/50 uppercase tracking-widest mb-1">Setting</div>
                    <div className="text-xs font-semibold text-[#2D3142]/80">Dutch East Indies, 1914</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#C5A059]/50 uppercase tracking-widest mb-1">Status</div>
                    <div className="text-xs font-semibold text-[#2D3142]/80">Querying Agents</div>
                  </div>
                </div>
              </div>
              <BookNotify book="Wake of the Falcon" testid="wake-of-the-falcon" />
            </div>

          </div>
        </div>
      </section>

      {/* ── Academic Articles ── */}
      <section className="py-16 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="max-w-xl mx-auto">
            <GraduationCap size={32} className="text-[#C5A059] mx-auto mb-4" />
            <h3 className="font-serif text-2xl font-bold text-[#0A1628] mb-3">
              Articles &amp; Book Chapters
            </h3>
            <p className="text-[#2D3142] leading-relaxed mb-6">
              In addition to the book-length works above, Donald S. Inbody has contributed articles and book chapters to the fields of political science and civil-military relations. The full publication record is indexed on Google Scholar.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={SCHOLAR_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="books-scholar-cta"
                className="inline-flex items-center gap-2 bg-[#0A1628] text-[#F5F0E8] px-6 py-3 text-sm font-semibold hover:bg-[#2D3142] transition-colors"
              >
                <GraduationCap size={14} /> View Full Record on Google Scholar
              </a>
              <Link
                to="/papers"
                className="inline-flex items-center gap-2 border border-[#0A1628] text-[#0A1628] px-6 py-3 text-sm font-semibold hover:bg-[#0A1628] hover:text-[#F5F0E8] transition-colors"
              >
                <BookOpen size={14} /> Papers &amp; Manuscripts
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
